import { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { STATISTICS_EMPTY_OVERDUE_RANK } from '../../common/lib/statisticsEmptyStateCopy';
import formatCurrency from '../../../../utils/formatCurrency';
import { getOverdueChartDimensionLabel, OVERDUE_CHART_RANGE_MODE } from './lib/overdueChartConfig';
import { countUniqueOverdueOrders, findOverdueRankItemByKey, OVERDUE_RANK_OTHER_MEMBER_START_RANK } from './lib/overdueStatChartData';

const OVERDUE_RANK_SKELETON_ROW_COUNT = 5;

const OverdueRankListScroll = ({ fillSlot = false, children }) => (
	<div
		className={[
			'statistics-dashboard-overdue-rank-list__scroll',
			fillSlot ? 'statistics-dashboard-overdue-rank-list__scroll--fill-slot' : null,
		]
			.filter(Boolean)
			.join(' ')}
	>
		{children}
	</div>
);

OverdueRankListScroll.propTypes = {
	fillSlot: PropTypes.bool,
	children: PropTypes.node,
};

const DashboardOverdueRankList = ({
	items = [],
	overdueData = {},
	isLoading = false,
	activeDimension,
	maxItems = 10,
	fillSlot = false,
	selectedKey = null,
	onSelectRow,
	rangeMode = OVERDUE_CHART_RANGE_MODE.OVERDUE_ONLY,
	className = '',
}) => {
	const dimensionLabel = getOverdueChartDimensionLabel(activeDimension);
	const [otherExpanded, setOtherExpanded] = useState(false);
	const selectedRow = useMemo(() => findOverdueRankItemByKey(items, selectedKey), [items, selectedKey]);
	const otherRow = useMemo(() => items.find((row) => row.key === '__other__') ?? null, [items]);

	useEffect(() => {
		setOtherExpanded(false);
	}, [activeDimension, items]);

	useEffect(() => {
		if (!selectedKey || !otherRow?.members?.length) return;
		if (otherRow.members.some((member) => member.key === selectedKey)) {
			setOtherExpanded(true);
		}
	}, [otherRow, selectedKey]);

	const listItems = useMemo(() => {
		if (selectedRow) return [selectedRow];
		return items.slice(0, maxItems);
	}, [items, maxItems, selectedRow]);

	const resolveRowRank = useCallback(
		(row) => {
			const index = items.findIndex((item) => item.key === row.key);
			if (index >= 0) return index + 1;
			const memberIndex = otherRow?.members?.findIndex((member) => member.key === row.key) ?? -1;
			if (memberIndex >= 0) return OVERDUE_RANK_OTHER_MEMBER_START_RANK + memberIndex;
			return 1;
		},
		[items, otherRow],
	);
	const totals = useMemo(() => {
		if (selectedRow) {
			return {
				arAmount: Number(selectedRow.arAmount ?? selectedRow.value) || 0,
				orderCount: Number(selectedRow.orderCount) || 0,
			};
		}
		return {
			arAmount: items.reduce((sum, row) => sum + (Number(row.arAmount ?? row.value) || 0), 0),
			orderCount: countUniqueOverdueOrders(overdueData, rangeMode),
		};
	}, [items, overdueData, rangeMode, selectedRow]);

	const handleRowClick = useCallback(
		(row) => {
			if (!onSelectRow) return;
			onSelectRow(selectedKey === row.key ? null : row.key);
		},
		[onSelectRow, selectedKey],
	);

	const toggleOtherExpanded = useCallback((event) => {
		event.preventDefault();
		event.stopPropagation();
		setOtherExpanded((prev) => !prev);
	}, []);

	const renderRankRow = useCallback(
		(row, { rank, isMember = false } = {}) => {
			const isOtherRow = row.key === '__other__';
			const canExpandOther = isOtherRow && Boolean(row.members?.length);
			const isSelected = selectedKey === row.key;
			const rowInteractive = Boolean(onSelectRow) && (!canExpandOther || isMember);

			return (
				<li
					key={isMember ? `member-${row.key}` : row.key}
					className={[
						'statistics-dashboard-overdue-rank-list__row',
						isOtherRow ? 'statistics-dashboard-overdue-rank-list__row--other' : null,
						canExpandOther && otherExpanded ? 'statistics-dashboard-overdue-rank-list__row--other-expanded' : null,
						isMember ? 'statistics-dashboard-overdue-rank-list__row--member' : null,
						rank <= 3 && !isMember ? 'statistics-dashboard-overdue-rank-list__row--top' : null,
						isSelected ? 'statistics-dashboard-overdue-rank-list__row--selected' : null,
						rowInteractive ? 'statistics-dashboard-overdue-rank-list__row--interactive' : null,
					]
						.filter(Boolean)
						.join(' ')}
					onClick={rowInteractive ? () => handleRowClick(row) : undefined}
					onKeyDown={
						rowInteractive
							? (event) => {
									if (event.key === 'Enter' || event.key === ' ') {
										event.preventDefault();
										handleRowClick(row);
									}
								}
							: undefined
					}
					role={rowInteractive ? 'button' : undefined}
					tabIndex={rowInteractive ? 0 : undefined}
					aria-pressed={rowInteractive ? isSelected : undefined}
				>
					<span className='statistics-dashboard-overdue-rank-list__rank' aria-hidden='true'>
						{rank}
					</span>
					<span className='statistics-dashboard-overdue-rank-list__label'>
						{canExpandOther ? (
							<button
								type='button'
								className='statistics-dashboard-overdue-rank-list__other-toggle'
								onClick={toggleOtherExpanded}
								aria-expanded={otherExpanded}
								aria-label={otherExpanded ? '收起其他明细' : '展开其他明细'}
							>
								<FontAwesomeIcon icon={otherExpanded ? 'chevron-down' : 'chevron-right'} className='statistics-dashboard-overdue-rank-list__other-toggle-icon' />
								<span className='statistics-dashboard-overdue-rank-list__other-toggle-label' title={row.label}>
									{row.label}
								</span>
							</button>
						) : (
							<span title={row.label}>{row.label}</span>
						)}
					</span>
					<span
						className={[
							'statistics-dashboard-overdue-rank-list__amount text-end',
							canExpandOther && onSelectRow ? 'statistics-dashboard-overdue-rank-list__metric-action' : null,
						]
							.filter(Boolean)
							.join(' ')}
						onClick={
							canExpandOther && onSelectRow
								? (event) => {
										event.stopPropagation();
										handleRowClick(row);
									}
								: undefined
						}
						onKeyDown={
							canExpandOther && onSelectRow
								? (event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											event.stopPropagation();
											handleRowClick(row);
										}
									}
								: undefined
						}
						role={canExpandOther && onSelectRow ? 'button' : undefined}
						tabIndex={canExpandOther && onSelectRow ? 0 : undefined}
						aria-pressed={canExpandOther && onSelectRow ? isSelected : undefined}
					>
						{formatCurrency(row.arAmount ?? row.value ?? 0)}
					</span>
					<span
						className={[
							'statistics-dashboard-overdue-rank-list__count text-end',
							canExpandOther && onSelectRow ? 'statistics-dashboard-overdue-rank-list__metric-action' : null,
						]
							.filter(Boolean)
							.join(' ')}
						onClick={
							canExpandOther && onSelectRow
								? (event) => {
										event.stopPropagation();
										handleRowClick(row);
									}
								: undefined
						}
						onKeyDown={
							canExpandOther && onSelectRow
								? (event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											event.stopPropagation();
											handleRowClick(row);
										}
									}
								: undefined
						}
						role={canExpandOther && onSelectRow ? 'button' : undefined}
						tabIndex={canExpandOther && onSelectRow ? 0 : undefined}
						aria-pressed={canExpandOther && onSelectRow ? isSelected : undefined}
					>
						{row.orderCount ?? 0} 单
					</span>
				</li>
			);
		},
		[handleRowClick, onSelectRow, otherExpanded, selectedKey, toggleOtherExpanded],
	);

	if (isLoading) {
		return (
			<div
				className={['statistics-dashboard-overdue-rank-list statistics-dashboard-overdue-rank-list--skeleton', className].filter(Boolean).join(' ')}
				role='status'
				aria-busy='true'
				aria-label='加载超期排行'
			>
				<div className='statistics-dashboard-overdue-rank-list__head'>
					<span className='statistics-dashboard-overdue-rank-list__head-rank'>#</span>
					<span>{dimensionLabel}</span>
					<span className='text-end'>应收账款</span>
					<span className='text-end'>单数</span>
				</div>
				<OverdueRankListScroll fillSlot={fillSlot}>
					<div className='statistics-dashboard-overdue-rank-list__body'>
						{Array.from({ length: OVERDUE_RANK_SKELETON_ROW_COUNT }, (_, index) => (
							<div
								key={index}
								className='statistics-dashboard-overdue-rank-list__row statistics-dashboard-overdue-rank-list__row--skeleton'
								aria-hidden='true'
							>
								<span className='statistics-dashboard-overdue-rank-list__skeleton statistics-dashboard-overdue-rank-list__skeleton--rank' />
								<span
									className='statistics-dashboard-overdue-rank-list__skeleton statistics-dashboard-overdue-rank-list__skeleton--label'
									style={{ width: `${52 + (index % 3) * 14}%` }}
								/>
								<span className='statistics-dashboard-overdue-rank-list__skeleton statistics-dashboard-overdue-rank-list__skeleton--amount' />
								<span className='statistics-dashboard-overdue-rank-list__skeleton statistics-dashboard-overdue-rank-list__skeleton--count' />
							</div>
						))}
					</div>
				</OverdueRankListScroll>
				<div className='statistics-dashboard-overdue-rank-list__foot' aria-label='排行合计'>
					<span className='statistics-dashboard-overdue-rank-list__foot-label'>合计</span>
					<span />
					<span className='statistics-dashboard-overdue-rank-list__skeleton statistics-dashboard-overdue-rank-list__skeleton--amount' />
					<span className='statistics-dashboard-overdue-rank-list__skeleton statistics-dashboard-overdue-rank-list__skeleton--count' />
				</div>
			</div>
		);
	}

	if (!listItems.length) {
		return (
			<div className={['statistics-dashboard-overdue-rank-list statistics-dashboard-overdue-rank-list--empty', className].filter(Boolean).join(' ')}>
				<span className='text-muted small'>{STATISTICS_EMPTY_OVERDUE_RANK}</span>
			</div>
		);
	}

	return (
		<div
			className={[
				'statistics-dashboard-overdue-rank-list',
				selectedKey ? 'statistics-dashboard-overdue-rank-list--filtered' : null,
				className,
			]
				.filter(Boolean)
				.join(' ')}
			role='region'
			aria-label={selectedRow ? `${dimensionLabel}超期排行筛选结果` : `${dimensionLabel}超期排行列表`}
		>
			<div className='statistics-dashboard-overdue-rank-list__head'>
				<span className='statistics-dashboard-overdue-rank-list__head-rank'>#</span>
				<span>{dimensionLabel}</span>
				<span className='text-end'>应收账款</span>
				<span className='text-end'>单数</span>
			</div>
			<OverdueRankListScroll fillSlot={fillSlot}>
				<ol className='statistics-dashboard-overdue-rank-list__body'>
					{listItems.map((row) => {
						const rank = resolveRowRank(row);
						return (
							<Fragment key={row.key}>
								{renderRankRow(row, { rank })}
								{row.key === '__other__' && otherExpanded
									? row.members?.map((member, memberIndex) =>
											renderRankRow(member, {
												rank: OVERDUE_RANK_OTHER_MEMBER_START_RANK + memberIndex,
												isMember: true,
											}),
										)
									: null}
							</Fragment>
						);
					})}
				</ol>
			</OverdueRankListScroll>
			<div className='statistics-dashboard-overdue-rank-list__foot' aria-label={selectedRow ? '筛选合计' : '排行合计'}>
				<span className='statistics-dashboard-overdue-rank-list__foot-label'>{selectedRow ? '筛选' : '合计'}</span>
				<span />
				<span className='statistics-dashboard-overdue-rank-list__amount text-end'>{formatCurrency(totals.arAmount)}</span>
				<span className='statistics-dashboard-overdue-rank-list__count text-end'>{totals.orderCount} 单</span>
			</div>
		</div>
	);
};

DashboardOverdueRankList.propTypes = {
	items: PropTypes.array,
	overdueData: PropTypes.object,
	isLoading: PropTypes.bool,
	activeDimension: PropTypes.string,
	maxItems: PropTypes.number,
	fillSlot: PropTypes.bool,
	selectedKey: PropTypes.string,
	onSelectRow: PropTypes.func,
	rangeMode: PropTypes.string,
	className: PropTypes.string,
};

export default memo(DashboardOverdueRankList);
